import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from '@/hooks/auth/useAuth';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { resolveContentScanners } from '@/lib/content-scanners';
import { resolveContextUrlMatchers } from '@/lib/context-url-matchers';
import { resolveContextMentionRenderers } from '@/lib/context-mention-renderers';
import type { PluginRegistryValue } from '@reflex/ui/client/interfaces';
import {
  collectPluginWebWiringIssuesDetailed,
  resolveRoutes,
  resolveRootLevelRoutes,
  resolvePublicRoutes,
  resolveDocsPublicRoutes,
  resolveChromelessRoutes,
  resolveMarketingPages,
  resolveNavItems,
  resolveCommands,
  resolveOverviewCards,
  resolveAgentActions,
  resolveAdminUserActions,
  resolveAgentTabs,
  resolveSettingsSections,
  resolveOrgSettingsSections,
  resolveSessionSharingSettings,
  resolveProfileSections,
  resolveAgentDetailSections,
  resolveAgentHeaderBadges,
  resolveAssociationChips,
  resolveHomeWidgets,
  resolveAttachments,
  resolveAgentTypes,
  resolveEventLabels,
  resolveSearchProviders,
  resolveLaunchMentionProviders,
  resolveAgentContextProviders,
  resolveChatMentionProviders,
  resolveFlowTriggerRenderers,
  resolvePageTabs,
  resolveToolCallRenderers,
  resolveResourcePreviews,
  resolveEntityIdPreviews,
  resolveOwnerKinds,
  resolveOriginators,
  resolveUserSetupSteps,
  resolveMarketingPreviews,
  resolveGlobalUiElements,
  resolveNavUtilityElements,
  type PluginManifest,
} from '@/lib/plugin-registry';

const EMPTY_MANIFESTS: PluginManifest[] = [];

/**
 * sessionStorage key holding a *signature* of the exact gap set the reporter
 * last auto-reloaded for this session. Keyed by signature (not a bare boolean)
 * so a changed gap set — a new missing component after another branch switch,
 * or the same gaps disappearing — is always eligible for a fresh recovery
 * attempt. A stale flag can therefore never wedge the session into a permanent
 * throw, and older sessions whose stored value predates this scheme auto-heal
 * (their value can never equal a real signature, so they reload once and
 * recover). Prevents an infinite reload loop because a genuine, unchanging gap
 * only reloads once per distinct signature.
 */
const WIRING_GAP_RELOAD_KEY = 'plugin-registry-stale-reload';

/**
 * sessionStorage key counting how many auto-recovery reloads we've fired this
 * session. The signature guard above re-arms whenever the gap *set* changes, so
 * on its own it cannot bound a reload loop: an actively-edited dev tree can
 * present a slightly different transient gap set on each reload (HMR / Vite
 * serving mid-transform), producing a new signature every time and reloading
 * forever — the page's loading bar never finishes. This hard per-session cap
 * makes a loop impossible: one reload still recovers the common stale-snapshot
 * case, and anything beyond that logs and carries on. Reset on a clean (or
 * optional-only) boot so a genuinely new staleness later in the session can
 * still recover once.
 */
const WIRING_GAP_RELOAD_COUNT_KEY = 'plugin-registry-stale-reload-count';
const MAX_AUTO_RELOADS_PER_SESSION = 1;

/** Clear both auto-recovery guards (signature + reload count). */
function clearWiringReloadGuards() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(WIRING_GAP_RELOAD_KEY);
  sessionStorage.removeItem(WIRING_GAP_RELOAD_COUNT_KEY);
}

// Walk every enabled manifest exactly once after manifests load and surface
// any componentPath/icon entries that aren't backed by a bundled
// `register.ts`. This catches stale workspace symlinks (the kind a plain
// `pnpm install` from a workspace root can leave behind) at boot instead of
// only when the user navigates to the broken page.
//
// In dev, the most common cause is the `import.meta.glob({ eager: true })`
// snapshot staleness documented at the bottom of `plugin-registry.ts` — a
// branch switch or dev-server restart left the running tab with an old
// `componentMap` that's missing freshly-added component paths. We auto-recover
// by reloading once per distinct required-gap signature; if a required gap
// survives the reload we log loudly but never throw — a wiring diagnostic must
// not brick the app, since every resolver already hides its missing component.
function reportWiringGapsOnce(manifests: PluginManifest[], reportedRef: { current: boolean }) {
  if (reportedRef.current) return;
  if (manifests.length === 0) return;
  reportedRef.current = true;

  const issues = manifests.flatMap(collectPluginWebWiringIssuesDetailed);
  if (issues.length === 0) {
    // Clear the auto-recover guards once we've seen a clean boot so future
    // staleness (e.g. another branch switch later in the day) can recover.
    clearWiringReloadGuards();
    return;
  }

  const requiredMessages = issues.filter((i) => !i.optional).map((i) => i.message);
  const message =
    `[plugin-registry] ${issues.length} web wiring gap(s) detected at boot:\n` +
    issues.map((i) => '  - ' + i.message).join('\n') +
    `\n\nFix the plugin's web/register.ts mappings, or run 'pnpm install' ` +
    `from the repo root and restart the dev server.`;

  console.error(message);

  if (!import.meta.env.DEV || import.meta.env.MODE === 'test') {
    // Prod logs only — a wiring gap must never brick a user session. Tests opt
    // out so recovery side effects don't leak across test boundaries.
    return;
  }

  // Optional gaps (an inline "Add new …" row, an attachment summary chip) are
  // dropped gracefully by their resolvers and reported once above — not worth a
  // disruptive full-page reload. If ONLY optional components are unwired, clear
  // any stale guard and carry on; the app is fully functional without them.
  if (requiredMessages.length === 0) {
    clearWiringReloadGuards();
    return;
  }

  // A REQUIRED component is unwired. The dominant dev cause is a stale
  // `import.meta.glob` snapshot from a branch switch or dev-server restart; a
  // full reload rebuilds the map from a fresh module graph. Guard by a
  // signature of the required gap set so a *changed* set always earns a fresh
  // attempt and the session can never wedge on a stale flag (the trap that used
  // to require a manual `sessionStorage.clear()`).
  const signature = [...requiredMessages].sort().join('\n');
  const priorSignature =
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(WIRING_GAP_RELOAD_KEY) : null;
  const reloadCount =
    typeof sessionStorage !== 'undefined'
      ? Number(sessionStorage.getItem(WIRING_GAP_RELOAD_COUNT_KEY) ?? '0')
      : MAX_AUTO_RELOADS_PER_SESSION;

  // Reload only if the gap set is new AND we haven't already spent this
  // session's single recovery reload. The hard count cap is what breaks the
  // loop: while editing, HMR churn can surface a *different* transient gap set
  // on each reload, so the signature keeps changing and — without the cap —
  // would reload forever, leaving the page's loading bar spinning indefinitely.
  if (
    typeof sessionStorage !== 'undefined' &&
    priorSignature !== signature &&
    reloadCount < MAX_AUTO_RELOADS_PER_SESSION
  ) {
    sessionStorage.setItem(WIRING_GAP_RELOAD_KEY, signature);
    sessionStorage.setItem(WIRING_GAP_RELOAD_COUNT_KEY, String(reloadCount + 1));
    console.warn(
      '[plugin-registry] auto-reloading once to rebuild componentMap from a fresh module graph',
    );
    location.reload();
    return;
  }

  // Either we already reloaded once for this exact gap set, or we've used up
  // this session's single recovery reload while the gap set churned. This is
  // either a genuine registration bug or — common after a branch switch — Vite
  // serving a stale module transform that a plain reload can't bust (a
  // dev-server restart does). Either way we MUST NOT brick the app: every
  // resolver already drops its missing component, so the affected plugin
  // features simply stay hidden. Surface loudly and carry on.
  console.error(
    `[plugin-registry] ${requiredMessages.length} required web wiring gap(s) persisted after an ` +
      `auto-recovery reload; the affected plugin features are hidden but the app is otherwise ` +
      `fine. If these components exist on disk, restart the dev server: Vite can serve a stale ` +
      `module transform after a branch switch.`,
  );
}

export function usePlugins() {
  // Key and header derive from the same auth-state org so the cached
  // manifests always belong to the org they are keyed under; the URL can
  // disagree mid-redirect because this hook mounts above the router. See
  // `orgHeaders`.
  const orgKey = useOrgKey();
  const { activeOrganizationId } = useAuth();
  const {
    data: rawManifests = EMPTY_MANIFESTS,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.plugins(orgKey),
    queryFn: () => api.getPlugins(activeOrganizationId),
    staleTime: Infinity,
  });

  // Boundary invariant: `usePlugins` always hands its callers a
  // `PluginManifest[]`. The facade `api.getPlugins` is typed
  // `Array<PluginManifest>`, but the value crosses a network boundary — a
  // misrouted request (no MSW handler in tests → Vite SPA fallback), a proxy
  // interstitial, or a future server regression can return a non-array body
  // (observed in practice: the Vite dev index.html string). Without this
  // guard, every `resolveX(manifests)` consumer below throws
  // `manifests.filter is not a function` and bricks the whole plugin
  // surface. Degrade to "no plugins" instead of crashing — the app stays
  // usable and react-query will refetch on the next key change.
  const manifests: PluginManifest[] = Array.isArray(rawManifests) ? rawManifests : EMPTY_MANIFESTS;

  const wiringReportedRef = useRef(false);
  useEffect(() => {
    reportWiringGapsOnce(manifests, wiringReportedRef);
  }, [manifests]);

  // Register plugin-contributed content scanners (file-path popovers, URL
  // unfurls, ...) into the shared `@reflex/ui` registry that backs
  // `MarkdownContent`. This is a side effect on a module singleton, so it runs
  // in an effect (not the render-time `useMemo` below) and reconciles cleanly
  // when manifests change (e.g. a plugin is enabled/disabled mid-session).
  useEffect(() => {
    resolveContentScanners(manifests);
  }, [manifests]);

  // Same shape, the write side: which pasted URLs the composer turns into
  // context chips. Also a module-singleton side effect, so also an effect.
  useEffect(() => {
    resolveContextUrlMatchers(manifests);
  }, [manifests]);

  // Custom mention-badge renderers: plugins can style their composer chips
  // with live state (e.g. PR chips colored by merged/closed/draft status).
  useEffect(() => {
    resolveContextMentionRenderers(manifests);
  }, [manifests]);

  const resolved = useMemo(() => {
    const chromeless = resolveChromelessRoutes(manifests);
    const resourcePreviews = resolveResourcePreviews(manifests);
    return {
      routes: resolveRoutes(manifests),
      rootLevelRoutes: resolveRootLevelRoutes(manifests),
      publicRoutes: resolvePublicRoutes(manifests),
      docsPublicRoutes: resolveDocsPublicRoutes(manifests),
      chromelessAuthedRoutes: chromeless.authed,
      chromelessPublicRoutes: chromeless.public,
      marketingPages: resolveMarketingPages(manifests),
      navItems: resolveNavItems(manifests),
      commands: resolveCommands(manifests),
      overviewCards: resolveOverviewCards(manifests),
      agentActions: resolveAgentActions(manifests),
      adminUserActions: resolveAdminUserActions(manifests),
      agentTabs: resolveAgentTabs(manifests),
      settingsSections: resolveSettingsSections(manifests),
      orgSettingsSections: resolveOrgSettingsSections(manifests),
      sessionSharingSettings: resolveSessionSharingSettings(manifests),
      profileSections: resolveProfileSections(manifests),
      agentDetailSections: resolveAgentDetailSections(manifests),
      agentHeaderBadges: resolveAgentHeaderBadges(manifests),
      associationChips: resolveAssociationChips(manifests),
      homeWidgets: resolveHomeWidgets(manifests),
      attachments: resolveAttachments(manifests),
      agentTypes: resolveAgentTypes(manifests),
      eventLabels: resolveEventLabels(manifests),
      searchProviders: resolveSearchProviders(manifests),
      launchMentionProviders: resolveLaunchMentionProviders(manifests),
      agentContextProviders: resolveAgentContextProviders(manifests),
      chatMentionProviders: resolveChatMentionProviders(manifests),
      flowTriggerRenderers: resolveFlowTriggerRenderers(manifests),
      pageTabs: resolvePageTabs(manifests),
      toolCallRenderers: resolveToolCallRenderers(manifests),
      resourcePreviews,
      entityIdPreviews: resolveEntityIdPreviews(resourcePreviews),
      ownerKinds: resolveOwnerKinds(manifests),
      originators: resolveOriginators(manifests),
      userSetupSteps: resolveUserSetupSteps(manifests),
      marketingPreviews: resolveMarketingPreviews(manifests),
      globalUiElements: resolveGlobalUiElements(manifests),
      navUtilityElements: resolveNavUtilityElements(manifests),
    };
  }, [manifests]);

  // The curated subset the `@reflex/ui/client` PluginRegistry context hands to
  // plugins. Built here, beside the resolve, so the host (App.tsx) passes it
  // straight through — keeping resolve and forward in one place so a field can't
  // be resolved but left unforwarded (which silently disables its contribution).
  const pluginRegistry = useMemo<PluginRegistryValue>(
    () => ({
      agentTypes: resolved.agentTypes,
      flowTriggerRenderers: resolved.flowTriggerRenderers,
      pageTabs: resolved.pageTabs,
      toolCallRenderers: resolved.toolCallRenderers,
      eventLabels: resolved.eventLabels,
      resourcePreviews: resolved.resourcePreviews,
      ownerKinds: resolved.ownerKinds,
    }),
    [resolved],
  );

  return {
    manifests,
    ...resolved,
    pluginRegistry,
    isLoading,
    isError,
  };
}
